import { MediaAsset } from 'libs/contentful/props';

/**
 * Contentful CDN domain names for image delivery.
 * Images are proxied through /_ctf-img/ for same-origin connection reuse.
 */
export const CONTENTFUL_DOMAINS = {
  IMAGES: 'images.ctfassets.net',
  DOWNLOADS: 'downloads.ctfassets.net',
} as const;

/**
 * Properties for the Contentful image loader
 */
export interface ContentfulImageLoaderProps {
  src: string;
  width: number;
  /** Optional aspect ratio (height / width) to maintain */
  aspectRatio?: number;
  /** Optional quality setting (1-100, defaults to 50) */
  quality?: number;
}

/**
 * Scale factor applied to image dimensions for higher resolution displays
 */
export const IMAGE_SCALE = 1.3;

/**
 * Maximum dimension for Contentful images after scaling.
 * Contentful has a 4000px limit for image dimensions.
 */
const MAX_SCALED_DIMENSION = 4000;

/**
 * Maximum dimension for GIF images after scaling.
 * Contentful has a 300 megapixel limit for animated GIFs (width × height × frames).
 * At dimensions of ~1600px and above, GIFs can exceed this limit depending on frame count.
 * Using 1500px as a conservative cap to ensure GIFs stay within limits.
 * @see https://www.contentful.com/developers/docs/technical-limits/
 */
const MAX_GIF_DIMENSION = 1500;

const isGifUrl = (url: string): boolean => url.toLowerCase().includes('.gif');

/**
 * Creates an image loader function with aspect ratio handling
 * @param media The media asset containing dimensions and metadata
 * @param aspectRatio Optional aspect ratio override (height / width). If not provided, calculates from media dimensions or defaults to 1
 * @returns A loader function that generates optimized Contentful image URLs
 */
export const createAspectRatioImageLoader = (media: MediaAsset, aspectRatio?: number) => {
  return ({ src, width, quality }: ContentfulImageLoaderProps) => {
    const ratio = aspectRatio || (media?.width && media?.height ? calculateAspectRatio(media.width, media.height) : 1);
    const result = buildContentfulImageUrl({ src, width, aspectRatio: ratio, quality });
    return typeof result === 'string' ? result : result.href;
  };
};

/**
 * Default Next.js image loader for Contentful images
 * Generates an optimized image URL without aspect ratio constraints
 * @param src The source URL of the image
 * @param width The desired width of the image
 * @param quality Optional quality setting (1-100, defaults to 50)
 * @returns The optimized Contentful image URL
 */
export default function contentfulImageLoader({ src, width, quality }: ContentfulImageLoaderProps) {
  const result = buildContentfulImageUrl({ src, width, quality });
  return typeof result === 'string' ? result : result.href;
}

/**
 * Builds an optimized Contentful image URL with scaling, format conversion, and dimension constraints
 * - Replaces downloads domain with images domain for optimization
 * - Converts images to WebP format (except SVGs); GIFs become animated WebP
 * - Applies 2x scaling for high-resolution displays
 * - Caps dimensions at 4000px while preserving aspect ratio (1500px for GIFs)
 * - GIFs use a lower dimension cap due to Contentful's 300 megapixel limit for animated GIFs
 * @param src The source URL of the image
 * @param width The desired width of the image
 * @param aspectRatio Optional aspect ratio (height / width) to maintain
 * @param quality Optional quality setting (1-100, defaults to 50)
 * @returns URL object with optimized image parameters, or string for local paths
 * @see https://www.contentful.com/developers/docs/technical-limits/
 */
function buildContentfulImageUrl({ src, width, aspectRatio, quality }: ContentfulImageLoaderProps): URL | string {
  // If the URL is already a proxy path (e.g., from a previous loader invocation),
  // return it as-is. This happens when EmbeddedAsset.tsx manually calls the loader
  // and then Next.js Image component invokes the global loader again.
  // Handles both relative paths (SSR) and absolute URLs (browser context).
  if (src.includes('/_ctf-img/')) {
    return src;
  }

  // some urls come w/o https scheme and new URL breaks w/o https
  const formattedSrc = src.startsWith('//') ? `https:${src}` : src;

  // If this is a local path, use Next.js image optimization API
  // Check if it's already a Next.js processed path (_next/static) or needs optimization
  if (
    formattedSrc.startsWith('/') ||
    (!formattedSrc.includes('http') &&
      formattedSrc !== CONTENTFUL_DOMAINS.IMAGES &&
      formattedSrc !== CONTENTFUL_DOMAINS.DOWNLOADS)
  ) {
    // If it's already a Next.js processed static asset (_next/static), return as-is
    if (formattedSrc.startsWith('/_next/static')) {
      return formattedSrc;
    }

    const height = aspectRatio ? calculateHeightFromAspectRatio(aspectRatio, width) : undefined;
    const scaledWidth = Math.round(width * IMAGE_SCALE);
    const scaledHeight = height ? Math.round(height * IMAGE_SCALE) : undefined;
    const { finalWidth, finalHeight } = scaleDownIfNeeded(scaledWidth, scaledHeight, formattedSrc);

    const params = new URLSearchParams({
      url: formattedSrc,
      w: finalWidth.toString(),
      q: (quality || 50).toString(),
    });

    if (finalHeight) {
      params.set('h', finalHeight.toString());
    }

    // Return Next.js image optimization API URL
    return `/_next/image?${params.toString()}`;
  }

  // Replace downloads.ctfassets.net with images.ctfassets.net for image optimization
  const optimizedSrc = formattedSrc.replace(CONTENTFUL_DOMAINS.DOWNLOADS, CONTENTFUL_DOMAINS.IMAGES);

  const url = new URL(optimizedSrc);
  const isContentfulImage = url.hostname === CONTENTFUL_DOMAINS.IMAGES;

  // If the image is not an SVG, convert it to WebP
  // GIFs are converted to animated WebP by Contentful's Images API
  if (!url.pathname.endsWith('.svg')) {
    url.searchParams.set('fm', 'webp');
  }

  const height = aspectRatio ? calculateHeightFromAspectRatio(aspectRatio, width) : undefined;
  const scaledWidth = Math.round(width * IMAGE_SCALE);
  const scaledHeight = height ? Math.round(height * IMAGE_SCALE) : undefined;
  const { finalWidth, finalHeight } = scaleDownIfNeeded(scaledWidth, scaledHeight, url.href);

  url.searchParams.set('w', finalWidth.toString());
  if (finalHeight) {
    url.searchParams.set('h', finalHeight.toString());
  }

  url.searchParams.set('fit', 'fill');
  url.searchParams.set('q', (quality || 50).toString());

  // Proxy Contentful images through same origin for HTTP/2 connection reuse.
  // Always return a relative path so the browser resolves it against its current origin.
  // This ensures consistent URLs between SSR and client (no hydration mismatch)
  // and works correctly in all environments (dev, staging, production).
  if (isContentfulImage) {
    const proxyPath = `/_ctf-img${url.pathname}`;
    const queryString = url.search;
    return `${proxyPath}${queryString}`;
  }

  return url;
}

const scaleDownIfNeeded = (scaledWidth: number, scaledHeight: number | undefined, url: string) => {
  // Use lower limit for GIFs due to Contentful's 300 megapixel limit for animated GIFs
  const maxDimension = isGifUrl(url) ? MAX_GIF_DIMENSION : MAX_SCALED_DIMENSION;

  const someDimensionExceedsLimit = scaledWidth > maxDimension || (scaledHeight && scaledHeight > maxDimension);
  if (!someDimensionExceedsLimit) {
    return { finalWidth: scaledWidth, finalHeight: scaledHeight };
  }

  const widthRatio = maxDimension / scaledWidth;
  const heightRatio = scaledHeight ? maxDimension / scaledHeight : 1;
  const scaleFactor = Math.min(widthRatio, heightRatio);

  return {
    finalWidth: Math.min(Math.round(scaledWidth * scaleFactor), maxDimension),
    finalHeight: scaledHeight ? Math.min(Math.round(scaledHeight * scaleFactor), maxDimension) : undefined,
  };
};

const calculateAspectRatio = (width: number, height: number): number => {
  return height / width;
};

const calculateHeightFromAspectRatio = (aspectRatio: number, width: number): number => {
  return Math.round(width * aspectRatio);
};
